import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import ThemeToggle from '../ui/ThemeToggle';
import NotificationBell from '../ui/NotificationBell';
import FloatingChat from '../ui/FloatingChat';
import { Menu, X, LayoutDashboard, Building2, DollarSign, BarChart3, MessageSquare, GraduationCap, Package, Megaphone, FileText, Users, LogOut } from 'lucide-react';
import { Badge } from '../ui/badge';

const DashboardLayout = ({ children }) => {
  const { user, token, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Role groups for easier menu configuration
  const HQ_ROLES = ['HQ_ADMIN', 'SYSTEM_ADMIN', 'REGIONAL_COORDINATOR'];
  const FRANCHISEE_ROLES = ['FRANCHISEE_OPERATOR', 'FRANCHISEE_MANAGER', 'FRANCHISEE_SUPERVISOR', 'FRANCHISEE'];
  const ALL_ROLES = [...HQ_ROLES, ...FRANCHISEE_ROLES];

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/', roles: ALL_ROLES },
    { icon: Building2, label: 'Franchisees', path: '/franchisees', roles: [...HQ_ROLES, 'FRANCHISEE_OPERATOR', 'FRANCHISEE_MANAGER'] },
    { icon: DollarSign, label: 'Financial', path: '/financial', roles: ['HQ_ADMIN', 'SYSTEM_ADMIN', ...FRANCHISEE_ROLES] },
    { icon: BarChart3, label: 'KPI & Performance', path: '/kpi', roles: ALL_ROLES },
    { icon: MessageSquare, label: 'Complaints', path: '/complaints', roles: ALL_ROLES },
    { icon: GraduationCap, label: 'Training', path: '/training', roles: ['HQ_ADMIN', 'SYSTEM_ADMIN', ...FRANCHISEE_ROLES] },
    { icon: Package, label: 'Procurement', path: '/procurement', roles: ['HQ_ADMIN', 'SYSTEM_ADMIN', 'REGIONAL_COORDINATOR', ...FRANCHISEE_ROLES] },
    { icon: Megaphone, label: 'Announcements', path: '/announcements', roles: ALL_ROLES },
    { icon: FileText, label: 'Documents', path: '/documents', roles: ALL_ROLES },
    { icon: Users, label: 'User Management', path: '/users', roles: ['HQ_ADMIN', 'SYSTEM_ADMIN', 'FRANCHISEE_OPERATOR'] },
  ];

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } bg-card border-r border-border w-64`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h1 className="text-xl font-heading font-bold text-primary tracking-tight">Little Caliphs</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Franchise Management</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg mb-2">
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.role.replace('_', ' ')}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-md border-b border-border/50">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="toggle-sidebar-btn"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground">
                  {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Notifications */}
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-8 lg:p-12">
          {children}
        </main>
      </div>

      {/* Floating Chat */}
      <FloatingChat />
    </div>
  );
};

export default DashboardLayout;